import { getRandomItem } from "@certn/utils";
import { css } from "@emotion/css";
import { PropsWithChildren } from "react";

import Constants from "../constants";

interface Props<T> extends PropsWithChildren {
  Component: React.ComponentType<any>;
  componentProps: T;
  type?: "layout" | "control";
  hidden?: boolean;
}

export function DebugWrapper<T extends { path: string; data?: unknown }>(
  props: Props<T>
) {
  const { Component, componentProps } = props;

  return (
    <div className={styles.wrapper} data-hidden={props.hidden}>
      <Component {...props} {...componentProps} />
      {Constants.debug && process.env.NODE_ENV === "development" && (
        <div
          className={css(
            styles.debugWrapper(Component.displayName),
            props.type === "layout" && styles.layout
          )}
        >
          <div>
            {[componentProps.path, Component.displayName]
              .filter(Boolean)
              .join(" / ")}
          </div>
          {props.type === "control" && (
            <div>{JSON.stringify(componentProps.data)}</div>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  debugWrapper: (displayName: string = "Unknown") =>
    css({
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
      top: 0,
      textAlign: "right",
      fontFamily: "monospace",
      fontSize: 8,
      color: getDebugColour(displayName),
      borderWidth: 1,
      borderStyle: "dashed",
      padding: 3,
      pointerEvents: "none",
    }),
  layout: css({
    left: -8,
    right: -8,
    bottom: -9,
    top: -8,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "flex-end",
  }),
  wrapper: css({
    position: "relative",
    "&[data-hidden=true]": { display: "none" },
  }),
};

function getDebugColour(displayName: string) {
  return getRandomItem(
    [
      "#5DA5AD",
      "#9C7CED",
      "#62AD31",
      "#5482E1",
      "#D08D22",
      "#64D88A",
      "#FFE750",
      "#F9B64A",
      "#1BB793",
      "#0F3F44",
      "#568A8F",
    ],
    displayName
  );
}
